/**
 *  @class
 *  @function FacetsToggle
 */
class FacetsToggle {
  constructor() {
    const drawer = document.getElementById('Facet-Drawer');
    const button = document.getElementById('Facets-Toggle');
    if (!drawer || !button || button.dataset.facetsBound) return;
    button.dataset.facetsBound = 'true';
    button.addEventListener('click', (event) => {
      event.preventDefault();
      const form = drawer.querySelector('form[data-apply-on-submit]');
      if (form) {
        form.reset();
        form.querySelectorAll('price-slider').forEach(price => {
          const min = price.querySelector('.field__input_min');
          const max = price.querySelector('.field__input_max');
          price.querySelector('.price_slider').noUiSlider?.set([
            min.value || 0, max.value || price.slider.dataset.max,
          ]);
        });
      }
      document.body.classList.add('open-cc');
      drawer.classList.add('active');
      drawer.removeAttribute('inert');
      button.setAttribute('aria-expanded', 'true');
      drawer.querySelector('.side-panel-close')?.focus();
    });
    if (drawer.dataset.facetsBound) return;
    drawer.dataset.facetsBound = 'true';
    new MutationObserver(() => {
      if (drawer.classList.contains('active')) return;
      const trigger = document.getElementById('Facets-Toggle');
      trigger?.setAttribute('aria-expanded', 'false');
      trigger?.focus({ preventScroll: true });
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    drawer.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        FacetsToggle.close();
      } else if (event.target.matches('.side-panel-close') && ['Enter', ' '].includes(event.key)) {
        event.preventDefault();
        FacetsToggle.close();
      } else if (event.key === 'Tab') {
        const controls = Array.from(drawer.querySelectorAll('button, input, select, a[href], [tabindex="0"], summary'))
          .filter(node => !node.disabled && node.getClientRects().length);
        const target = event.shiftKey ? controls.at(-1) : controls[0];
        if (event.target === (event.shiftKey ? controls[0] : controls.at(-1))) {
          event.preventDefault();
          target?.focus();
        }
      }
    });
  }

  static close() {
    const drawer = document.getElementById('Facet-Drawer');
    drawer?.classList.remove('active');
    drawer?.setAttribute('inert', '');
    document.body.classList.remove('open-cc');
  }
}

/**
 *  @class
 *  @function SidebarToggle
 */
class SidebarToggle {
  constructor() {
    this.container = document.getElementById('Sidebar-Container');
    const button = document.getElementById('Sidebar-Toggle');

    // Add functionality to buttons
    if (button) {
      const span = button.querySelector('span');

      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.container.classList.toggle('facets--sidebar-open');

        span.textContent = this.container.classList.contains('facets--sidebar-open') ? theme.strings.hideFilters : theme.strings.showFilters;
      });
    }
  }
}

/**
 *  @class
 *  @function FacetFiltersForm
 */
class CollectionDiscovery {
  static random(seed) {
    let value = 2166136261;
    for (const character of String(seed)) value = Math.imul(value ^ character.charCodeAt(0), 16777619);
    return () => {
      value += 0x6D2B79F5;
      let next = Math.imul(value ^ value >>> 15, 1 | value);
      next ^= next + Math.imul(next ^ next >>> 7, 61 | next);
      return ((next ^ next >>> 14) >>> 0) / 4294967296;
    };
  }

  static pages(total, random) {
    return CollectionDiscovery.shuffle(Array.from({ length: total }, (_, index) => index + 1), random);
  }

  static shuffle(items, random) {
    const result = [...items];
    for (let index = result.length - 1; index > 0; index--) {
      const target = Math.floor(random() * (index + 1));
      [result[index], result[target]] = [result[target], result[index]];
    }
    return result;
  }

  static pageNumbers(total, current) {
    return Array.from({ length: total }, (_, index) => index + 1)
      .filter(page => page === 1 || page === total || Math.abs(page - current) <= 2);
  }

  static pagination(parsed, control, params, seed, current, total) {
    const nav = parsed.createElement('nav');
    nav.className = 'pagination collection-discovery-pages';
    nav.setAttribute('aria-label', control.dataset.label);
    const list = parsed.createElement('ul');
    list.className = 'page-numbers nav-links';
    let previous = 0;
    for (const page of CollectionDiscovery.pageNumbers(total, current)) {
      if (previous && page - previous > 1) {
        const gap = parsed.createElement('li');
        gap.className = 'ellip';
        gap.textContent = '…';
        list.append(gap);
      }
      const item = parsed.createElement('li');
      item.className = page === current ? 'current' : 'page';
      const label = parsed.createElement(page === current ? 'span' : 'a');
      label.textContent = page;
      if (page === current) {
        label.setAttribute('aria-current', 'page');
        label.setAttribute('tabindex', '-1');
      } else {
        const linkParams = new URLSearchParams(params);
        linkParams.delete('section_id');
        linkParams.set('discover', seed);
        linkParams.set('page', page);
        label.href = `${window.location.pathname}?${linkParams}`;
        label.dataset.discoverPage = page;
      }
      item.append(label);
      list.append(item);
      previous = page;
    }
    nav.append(list);
    return nav;
  }

  static async render(html, params, seed, signal, requestedPage = 1) {
    const parser = new DOMParser();
    const parsed = parser.parseFromString(html, 'text/html');
    const control = parsed.querySelector('.collection-discover');
    if (!control) return html;
    const total = Number(control.dataset.pages);
    const current = Math.max(1, Math.min(total || 1, Math.floor(Number(requestedPage)) || 1));
    const offset = Math.floor((current - 1) / 3) * 3;
    const pages = CollectionDiscovery.pages(total, CollectionDiscovery.random(seed)).slice(offset, offset + 3);
    const documents = await Promise.all(pages.map(async page => {
      if (page === 1) return parsed;
      const pageParams = new URLSearchParams(params);
      pageParams.set('page', page);
      return parser.parseFromString(await FacetFiltersForm.fetchSection(pageParams, signal), 'text/html');
    }));
    const products = new Map();
    for (const page of documents) {
      if (!page.getElementById('product-grid')) throw new Error('Missing discovery page');
      for (const item of page.querySelectorAll('#product-grid > [data-pagination-key^="product:"]')) {
        products.set(item.dataset.paginationKey, item.cloneNode(true));
      }
    }
    const pageSize = Number(control.dataset.pageSize);
    const start = ((current - 1) % 3) * pageSize;
    const cards = CollectionDiscovery.shuffle(products.values(), CollectionDiscovery.random(`${seed}:${offset}`)).slice(start, start + pageSize);
    if (!cards.length && Number(control.dataset.count)) throw new Error('No discovery products returned');
    const grid = parsed.getElementById('product-grid');
    if (cards.length) {
      const overlay = grid.querySelector(':scope > .loading-overlay');
      const lifestyleImages = Array.from(grid.querySelectorAll(':scope > [data-collection-lifestyle]'));
      grid.replaceChildren(...cards, ...lifestyleImages);
      if (overlay) grid.append(overlay);
    }
    const summary = control.dataset.summary.replace('[count]', cards.length).replace('[total]', control.dataset.count);
    const resetParams = new URLSearchParams(params);
    resetParams.delete('section_id');
    resetParams.delete('page');
    resetParams.delete('discover');
    resetParams.set('sort_by', 'manual');
    control.dataset.active = 'true';
    control.querySelector('[data-discover]').textContent = control.dataset.again;
    const reset = control.querySelector('[data-discover-reset]');
    reset.hidden = false;
    reset.href = `${window.location.pathname}?${resetParams}`;
    control.querySelector('[role="status"]').textContent = summary;
    parsed.querySelectorAll('select[name="sort_by"]').forEach(select => {
      const option = parsed.createElement('option');
      option.value = '';
      option.textContent = control.dataset.label;
      option.disabled = true;
      select.querySelectorAll('option').forEach(item => item.removeAttribute('selected'));
      option.setAttribute('selected', 'selected');
      select.prepend(option);
    });
    parsed.querySelectorAll('input[type="radio"][name="sort_by"]').forEach(input => input.removeAttribute('checked'));
    const pagination = parsed.querySelector('.pagination--wrapper');
    if (pagination) {
      pagination.replaceChildren(control.cloneNode(true));
      if (total > 1) pagination.prepend(CollectionDiscovery.pagination(parsed, control, params, seed, current, total));
    }
    return parsed.documentElement.outerHTML;
  }

  static init() {
    document.addEventListener('click', event => {
      const page = event.target.closest('[data-discover-page]');
      if (page) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
        event.preventDefault();
        FacetFiltersForm.renderPage(new URL(page.href).search.slice(1), event);
        return;
      }
      const button = event.target.closest('[data-discover]');
      const reset = event.target.closest('[data-discover-reset]');
      if (button || reset) {
        event.preventDefault();
        if (button?.disabled) return;
        const params = new URLSearchParams(window.location.search);
        params.delete('page');
        if (reset) {
          params.delete('discover');
          params.set('sort_by', 'manual');
        } else {
          params.set('discover', crypto.getRandomValues(new Uint32Array(1))[0].toString(36));
        }
        FacetFiltersForm.renderPage(params.toString(), event);
      }
    });
    if (new URLSearchParams(window.location.search).has('discover')) {
      FacetFiltersForm.renderPage(window.location.search.slice(1), null, false);
    }
  }
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.addEventListener('click', event => {
      const button = event.target.closest('.thb-filter-content--button');
      if (button) this.onMore.call(button, event);
    });
    const form = this.querySelector('form');
    form.addEventListener('input', (event) => {
      this.enforceSingleColor(event.target);
      if (!form.hasAttribute('data-apply-on-submit')) this.debouncedOnSubmit(event);
    });
    form.addEventListener('submit', event => {
      this.onSubmitHandler(event);
      if (form.hasAttribute('data-apply-on-submit')) FacetsToggle.close();
    });
  }

  connectedCallback() {
    if (!this.classList.contains('facets--bar')) return;
    this.updateStickyOffset = this.updateStickyOffset.bind(this);
    this.stickyHeaders = Array.from(document.querySelectorAll('.shopify-section-group-header-group, theme-header'));
    this.stickyResizeObserver = new ResizeObserver(this.updateStickyOffset);
    this.stickyHeaders.forEach(header => this.stickyResizeObserver.observe(header));
    window.addEventListener('resize', this.updateStickyOffset);
    this.updateStickyOffset();
  }

  disconnectedCallback() {
    this.stickyResizeObserver?.disconnect();
    if (this.updateStickyOffset) window.removeEventListener('resize', this.updateStickyOffset);
  }

  updateStickyOffset() {
    const offset = this.stickyHeaders.reduce((bottom, header) => {
      const style = getComputedStyle(header);
      const top = parseFloat(style.top);
      if (!['sticky', 'fixed'].includes(style.position) || !Number.isFinite(top) || !header.getClientRects().length) return bottom;
      return Math.max(bottom, top + header.getBoundingClientRect().height);
    }, 0);
    this.style.setProperty('--facets-sticky-offset', `${offset}px`);
  }

  enforceSingleColor(input) {
    const presenter = window.BloomrVariantPresentation;
    if (!presenter?.isColorFilter(input.name) || !input.checked) return;
    this.querySelectorAll('input').forEach((other) => {
      if (other !== input && presenter.isColorFilter(other.name)) other.checked = false;
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = window.location.search.slice(1);
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
    const params = new URLSearchParams(window.location.search);
    const single = window.BloomrVariantPresentation?.singleColorParams(params);
    if (!params.has('discover') && single && single.toString() !== params.toString()) {
      FacetFiltersForm.renderPage(params.toString(), null, false);
    }
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static async renderPage(searchParams, event, updateURLHash = true) {
    const single = window.BloomrVariantPresentation?.singleColorParams(searchParams).toString();
    const normalizeURL = single !== undefined && single !== new URLSearchParams(searchParams).toString();
    searchParams = single ?? searchParams;
    const sections = FacetFiltersForm.getSections();
    const requestId = ++FacetFiltersForm.requestId;
    FacetFiltersForm.abortController?.abort();
    const controller = new AbortController();
    FacetFiltersForm.abortController = controller;
    const timeout = setTimeout(() => controller.abort('timeout'), 15000);
    FacetFiltersForm.setLoading(true);

    try {
      for (const section of sections) {
        const params = new URLSearchParams(searchParams);
        const seed = params.get('discover');
        const page = params.get('page');
        params.delete('discover');
        if (seed !== null) params.delete('page');
        params.set('section_id', section.section);
        let html = await FacetFiltersForm.fetchSection(params, controller.signal);
        if (seed !== null) html = await CollectionDiscovery.render(html, params, seed, controller.signal, page);
        if (requestId !== FacetFiltersForm.requestId) return;

        if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
        else if (normalizeURL) history.replaceState({ searchParams }, '', `${window.location.pathname}?${searchParams}${window.location.hash || ''}`);
        if (!FacetFiltersForm.renderProductGridContainer(html, event)) {
          throw new Error('Collection response did not contain a usable product grid');
        }
        FacetFiltersForm.searchParamsPrev = searchParams;
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductCount(html);
        if (event?.target.matches('form[data-apply-on-submit]')) {
          document.getElementById('Facets-Toggle')?.focus({ preventScroll: true });
        } else if (event?.target.closest('[data-discover], [data-discover-reset]')) {
          document.querySelector('[data-discover]')?.focus({ preventScroll: true });
        } else if (event?.target.closest('[data-discover-page]')) {
          document.querySelector('.collection-discovery-pages [aria-current="page"]')?.focus({ preventScroll: true });
        }
      }
    } catch (error) {
      if (requestId === FacetFiltersForm.requestId && (!controller.signal.aborted || controller.signal.reason === 'timeout')) {
        const control = document.querySelector('.collection-discover');
        if (control) {
          control.setAttribute('data-failed', '');
          control.querySelector('[role="status"]').textContent = control.dataset.error;
        }
        console.error('Error loading collection:', error);
      }
    } finally {
      clearTimeout(timeout);
      if (requestId === FacetFiltersForm.requestId) {
        FacetFiltersForm.setLoading(false);
        FacetFiltersForm.abortController = null;
      }
    }
  }

  static async fetchSection(params, signal) {
    const url = `${window.location.pathname}?${params}`;
    const cached = FacetFiltersForm.filterData.find(item => item.url === url);
    if (cached) return cached.html;
    const response = await fetch(url, { signal, headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Collection request failed with status ${response.status}`);
    const html = await response.text();
    if (!html.includes('id="ProductGridContainer"')) throw new Error('Missing collection section');
    FacetFiltersForm.filterData = [...FacetFiltersForm.filterData.slice(-7), { html, url }];
    return html;
  }

  static renderProductGridContainer(html, event) {
    const currentGrid = document.getElementById('ProductGridContainer');
    const incomingGrid = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');

    if (!currentGrid || !incomingGrid) return false;

    currentGrid.innerHTML = incomingGrid.innerHTML;

    const headerHeight = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
      10
    ) || 0;
    const top = currentGrid.getBoundingClientRect().top + document.documentElement.scrollTop - headerHeight - 30;

    currentGrid.querySelectorAll('.lazyload').forEach((image) => {
      if (window.lazySizes) {
        window.lazySizes.loader.unveil(image);
      }
    });

    if (event) {
      window.scrollTo({
        top,
        left: 0,
        behavior: 'smooth'
      });
    }

    return true;
  }

  static setLoading(isLoading) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    const collection = productGridContainer && productGridContainer.querySelector('.collection');

    if (collection) {
      collection.classList.toggle('loading', isLoading);
    }

    document.querySelectorAll('.thb-filter-count, .mobile-filter-count').forEach((item) => {
      item.classList.toggle('loading', isLoading);
    });

    document.querySelectorAll('.collection-discover').forEach(control => {
      if (isLoading) control.removeAttribute('data-failed');
      const button = control.querySelector('[data-discover]');
      button.disabled = isLoading || Number(control.dataset.count) === 0;
      button.textContent = isLoading ? control.dataset.loading : (control.dataset.active ? control.dataset.again : control.dataset.label);
    });
    if (productGridContainer) productGridContainer.setAttribute('aria-busy', String(isLoading));

    if (!isLoading) {
      FacetFiltersForm.toggleActiveFacets(false);
    }
  }

  static renderProductCount(html) {
    const countHtml = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const container = document.getElementsByClassName('thb-filter-count');

    if (countHtml) {
      for (var item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove('loading');
      }
    }

  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const incomingMobileForm = parsedHTML.getElementById('FacetFiltersFormMobile');
    const currentMobileForm = document.getElementById('FacetFiltersFormMobile');
    if (incomingMobileForm && currentMobileForm) {
      currentMobileForm.innerHTML = incomingMobileForm.innerHTML;
    }
    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    new FacetsToggle();
    new SidebarToggle();
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelectorAll(selector).forEach(element => {
        element.innerHTML = activeFacetsElement.innerHTML;
      });
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-filter-count'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const currentElement = document.querySelector(selector);
      if (!currentElement) return;
      currentElement.innerHTML = html.querySelector(selector).innerHTML;
      currentElement.classList.remove('loading');
    });

  }

  static updateURLHash(searchParams) {
    history.pushState({
      searchParams
    }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return [];

    return [
      {
        section: productGrid.dataset.id,
      }];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest('form'));
    const searchParams = window.BloomrVariantPresentation?.singleColorParams(
      new URLSearchParams(formData), event.target,
    ) || new URLSearchParams(formData);
    const discovery = new URLSearchParams(window.location.search).get('discover');
    const submittedForm = event.target.closest('form');
    const drawerSortChanged = submittedForm.hasAttribute('data-apply-on-submit') &&
      formData.has('sort_by') && formData.get('sort_by') !== document.querySelector('#SortByBar')?.value;
    const sortChanged = event.target.name === 'sort_by' || drawerSortChanged;
    if (discovery !== null && !sortChanged) searchParams.set('discover', discovery);
    searchParams.delete('page');
    const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) searchParams.set('preview_theme_id', preview);
    if (searchParams.get('filter.v.price.gte') === "0.00") {
      searchParams.delete('filter.v.price.gte');
    }
    const slider = event.target.closest('form').querySelector('.price_slider');
    if (slider) {
      if (searchParams.get('filter.v.price.lte') === parseFloat(slider.dataset.max).toFixed(2)) {
        searchParams.delete('filter.v.price.lte');
      }
    }
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const nextParams = new URL(event.currentTarget.href).searchParams;
    const params = window.BloomrVariantPresentation?.singleColorParams(
      nextParams,
    ) || nextParams;
    const discovery = new URLSearchParams(window.location.search).get('discover');
    if (discovery !== null) params.set('discover', discovery);
    params.delete('page');
    FacetFiltersForm.renderPage(params.toString(), event);
  }

  onMore(e) {
    let ul = this.closest('.thb-filter-content').querySelector('ul');

    if (ul.hasAttribute('filters-open')) {
      this.textContent = theme.strings.showMore;
      ul.removeAttribute('filters-open');
    } else {
      this.textContent = theme.strings.showLess;
      ul.setAttribute('filters-open', '');
    }
    e.preventDefault();
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.abortController = null;
FacetFiltersForm.requestId = 0;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
CollectionDiscovery.init();

/**
 *  @class
 *  @function FacetRemove
 */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('a').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        form.onActiveFilterClick(event);
      });
    });
  }
}

customElements.define('facet-remove', FacetRemove);


/**
 *  @class
 *  @function FacetToolbar
 */
class FacetToolbar extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.productGrid = this.closest('#ProductGridContainer');
    this.compareToggle = this.querySelector('#compare_toggle');
    this.layoutSwitcher = this.querySelector('.facets--bar-view-as');
    this.buttons = this.querySelectorAll('.facets--bar-view-as-button');
    this.productLayout = window.localStorage.getItem('product-layout');
    if (this.layoutSwitcher) {
      this.buttons.forEach((button) => {
        button.addEventListener('click', (evt) => {
          [].forEach.call(this.buttons, function (el) {
            el.removeAttribute('disabled');
          });
          this.onLayoutChange(button, button.dataset.value);
          evt.preventDefault();
        });
      });

      if (this.productLayout && this.productLayout === 'list') {
        const toggle = this.querySelector(`.facets--bar-view-as-button-${this.productLayout}`);
        if (toggle) toggle.click();
      }
    }
    if (this.compareToggle) {
      this.compareToggle.addEventListener('change', this.onCompare);
    }
  }
  onLayoutChange(button, value) {
    button.setAttribute('disabled', '');
    this.productGrid.dataset.layout = value;
    window.localStorage.setItem('product-layout', value);
  }
  onCompare(e) {
    document.body.classList.toggle('compare-true');
    e.preventDefault();
  }
}
customElements.define('facet-toolbar', FacetToolbar);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {

  constructor() {
    super();
  }
  connectedCallback() {
    this.slider = this.querySelector('.price_slider');
    this.amounts = this.querySelector('.price_slider_amount');
    this.form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    this.inputEvent = new CustomEvent('input');

    const args = {
      start: [
        parseFloat(this.slider.dataset.minValue || 0),
        parseFloat(this.slider.dataset.maxValue || this.slider.dataset.max)
      ],
      connect: true,
      step: 10,
      direction: document.dir,
      range: {
        'min': 0,
        'max': parseFloat(this.slider.dataset.max)
      }
    };
    noUiSlider.create(this.slider, args);

    this.slider.noUiSlider.on('update', this.onSliderUpdate.bind(this));
    this.slider.noUiSlider.on('change', this.onSliderChange.bind(this));

  }
  onSliderUpdate(values) {
    this.amounts.querySelector('.field__input_min').value = values[0];
    this.amounts.querySelector('.field__input_max').value = values[1];
  }

  onSliderChange(values) {
    this.form.querySelector('form').dispatchEvent(this.inputEvent);
  }
}
customElements.define('price-slider', PriceSlider);

new FacetsToggle();
new SidebarToggle();
